import { DestroyRef, inject, Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { FirebaseStorage, getDownloadURL, getStorage, ref, uploadBytes, uploadBytesResumable, UploadMetadata, UploadResult, uploadString, UploadTask } from 'firebase/storage';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { getDatabase, onValue, ref as dbRef, onChildAdded, remove, query, limitToLast, orderByChild, startAfter } from 'firebase/database';
import { deleteToken, getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getAnalytics } from "firebase/analytics";
import { FIREBASE } from '../app.config';
import { Capacitor } from '@capacitor/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { IndexedDBService } from './indexed-db.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface Target {
  id?: number
  type?: string
  document_id?: number
  document_type?: string
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  app = initializeApp(FIREBASE);
  db = getDatabase(this.app);
  firestore = getFirestore(this.app);
  auth = getAuth(this.app);
  messaging;
  storage: FirebaseStorage = getStorage(this.app);
  analytics = getAnalytics(this.app);
  messages$ = new Subject<any>();

  private readonly _indexedDBService = inject(IndexedDBService);

  constructor(
  ) {
    
  }
  init(company:any){
    this.storage = getStorage(this.app, company.bucket);
    if(!Capacitor.isNativePlatform()){
      console.log('[FCM] Starting Web Messaging');
      this.messaging = getMessaging(this.app);
      this.messages();
    }
  }
  uploadFile(file: File | Blob, path: string, metadata?: UploadMetadata | undefined): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileRef = ref(this.storage, path);
      const task = uploadBytes(fileRef, file, metadata);
      task.then((snapshot) => {
        getDownloadURL(snapshot.ref).then((url) => {
          resolve(url);
        }).catch((error) => {
          reject(error);
        })
      }).catch((error) => {
        reject(error);
      });
    });
  }
  uploadFileResumable(file: File | Blob, path: string, metadata?: UploadMetadata | undefined): UploadTask {
    const fileRef = ref(this.storage, path);
    return uploadBytesResumable(fileRef, file, metadata);
  }
  uploadString(data: string, path: string, format: 'data_url' | 'base64' | 'raw', metadata?: UploadMetadata | undefined): Promise<UploadResult>{
    const fileRef = ref(this.storage, path);
    return uploadString(fileRef, data, format, metadata);
  }
  object(path: string | undefined){
    const objectRef = dbRef(this.db, path);
    return new Observable((observer) => {
      onValue(objectRef, (snapshot) => {
        observer.next(snapshot.val());
      });
    });
  }
  objectAdded(path: string | undefined, startAt?){
    const objectRef = dbRef(this.db, path);
    const recentObjectsQuery = query(objectRef, orderByChild('created_at'), startAt ? startAfter(startAt) : limitToLast(1));
    return new Observable((observer) => {
      onChildAdded(recentObjectsQuery, (snapshot) => {
        const id = snapshot.key;
        const data = {
          id,
          ...snapshot.val()
        }
        observer.next(data);
      });
    });
  }
  removeObject(path: string | undefined){
    const objectRef = dbRef(this.db, path);
    return remove(objectRef);
  }
  getMesssagingToken(): Promise<string>{
    return new Promise((resolve,reject) => {
      getToken(
        this.messaging,{
          vapidKey: FIREBASE.vapidKey
        })
      .then((token:string) => {
        console.log('[FCM] Token Generated:', token);
        resolve(token);
      }).catch((error) => {
        reject(error);
      });
    })
  }
  deleteToken(){
    return deleteToken(this.messaging)
  }
  messages(){
    onMessage(this.messaging, (message) => {
      console.log('[FCM] Message received:', message);
      this.messages$.next(message);
    });
    console.log('[FCM] Listening for messages.');
  }
  signInWithCustomToken(token: string){
    return signInWithCustomToken(this.auth, token);
  }
  signOut(){
    return signOut(this.auth);
  }

  subscribeTo(ref, fn: Function, destroyRef: DestroyRef, fbFn = onValue, addKey=false, ...queryArgs) {
    
    const objectRef = dbRef(this.db, ref);
    return new Observable((observer) => {
      const queryRef = query(objectRef, ...queryArgs);
      fbFn(queryRef, (snapshot) => {
        if(addKey){
          const id = snapshot.key;
          const data = {
            id,
            ...snapshot.val()
          }
          observer.next(data);
          return;
        }else{
          observer.next(snapshot.val());
          return;
        }
      });
    }).pipe(
      takeUntilDestroyed(destroyRef)
    ).subscribe((message) => {
      if (message) {
        fn(message);
      }
    });
  }

  subscribeToNew(ref, startAt, fn: Function, destroyRef: DestroyRef) {
    const objectRef = dbRef(this.db, ref);
    return new Observable((observer) => {
      const recentCommentsQuery = query(objectRef, orderByChild('created_at'), startAt ? startAfter(startAt) : limitToLast(1));
      onChildAdded(recentCommentsQuery, (snapshot) => {
        observer.next(snapshot.val());
      });
    }).pipe(
      takeUntilDestroyed(destroyRef)
    ).subscribe((message) => {
      if (message) {
        fn(message);
      }
    });
  }

  

  subscribeToMessages(
    message_type: string,
    target: Target | Target[],
    fn: Function,
    destroyRef: DestroyRef
  ) {
      this.messages$.pipe(
        takeUntilDestroyed(destroyRef)
      ).subscribe((message) => {
        if (message.data.type !== message_type) { return }

        const data = JSON.parse(message.data.payload);
        if (!target || this.match(data, target)) {
          fn(data);
        }
      });

      fromEvent(window, 'focus').pipe(
        takeUntilDestroyed(destroyRef)
      ).subscribe(() => {
        this._indexedDBService.read('background_notifications').then((value) => {
          if (!value || value.length === 0) {
            return;
          }
          value.forEach((notification) => {
            const data = JSON.parse(notification.data.payload);
            if (this.match(data, target)) {
              fn(data);
              this._indexedDBService.delete('background_notifications', notification.id);
            }
          });
        });
      });
  }

  match(message_data, target: Target | Target[]): boolean {
    return target instanceof Array ? this.matchMultiple(message_data, target) : this.matchSingle(message_data, target);
  }

  matchMultiple(message_data, targets: Target[]): boolean {
    return targets.some((target) => this.matchSingle(message_data, target));
  }

  matchSingle(message_data, {id = null, type = null, document_id = null, document_type = null}: Target): boolean {
    return (message_data?.id?.toString() === id?.toString() && message_data?.type?.toString() === type?.toString())
    || (message_data?.document_id?.toString() === document_id?.toString()
        &&  message_data?.document_type?.toString() === document_type?.toString())
  }
}
